import { useRef, useState } from "react";
import { QuizImportSchema } from "@hootapp/shared";
import { api, ApiError } from "../../lib/api.js";
import { useT, type Translate } from "../../i18n/index.js";
import type { Quiz } from "./types.js";
import { errorText, ghostButton, primaryButton, surface, textInput } from "./quiz-ui.js";

const EXAMPLE = `{
  "title": "Family Trivia",
  "questions": [
    {
      "type": "quiz_single",
      "prompt": "Capital of France?",
      "timerSeconds": 20,
      "options": [
        { "label": "Paris", "isCorrect": true },
        { "label": "London" }
      ]
    },
    {
      "type": "type_answer",
      "prompt": "Chemical symbol for water?",
      "acceptedAnswers": ["H2O", "water"]
    },
    { "type": "slide", "prompt": "Round two" }
  ]
}`;

/**
 * Turns a Zod failure into something a person editing a file can act on:
 * which question, and what is wrong with it.
 */
function describeIssues(error: import("zod").ZodError, t: Translate): string[] {
  return error.issues.slice(0, 6).map((issue) => {
    const [root, index, ...rest] = issue.path;
    const where =
      root === "questions" && typeof index === "number"
        ? t("import.questionLabel", { number: index + 1 }) +
          (rest.length > 0 ? ` (${rest.join(".")})` : "")
        : issue.path.join(".") || t("import.fileLabel");
    return `${where}: ${issue.message}`;
  });
}

export interface QuizImportPanelProps {
  onImported: (quiz: Quiz) => void;
  onCancel: () => void;
}

export function QuizImportPanel({ onImported, onCancel }: QuizImportPanelProps) {
  const t = useT();
  const [text, setText] = useState("");
  const [errors, setErrors] = useState<string[]>([]);
  const [isImporting, setIsImporting] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const readFile = async (file: File) => {
    setErrors([]);
    try {
      setText(await file.text());
    } catch {
      setErrors([t("import.readFailed")]);
    }
  };

  const handleImport = async () => {
    setErrors([]);

    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch (err) {
      setErrors([`${t("import.notJson")} ${err instanceof Error ? err.message : ""}`.trim()]);
      return;
    }

    // Checked here as well as on the server so a mistyped file is answered
    // immediately, and with the offending question named.
    const result = QuizImportSchema.safeParse(parsed);
    if (!result.success) {
      setErrors(describeIssues(result.error, t));
      return;
    }

    setIsImporting(true);
    try {
      const data = await api.post<{ quiz: Quiz }>("/quizzes/import", result.data);
      onImported(data.quiz);
    } catch (err) {
      setErrors([err instanceof ApiError ? err.message : t("import.failed")]);
      setIsImporting(false);
    }
  };

  return (
    <section
      style={{ ...surface, padding: "1.25rem", display: "grid", gap: "0.85rem" }}
      aria-label={t("import.panelLabel")}
    >
      <div>
        <h2 style={{ margin: 0, fontSize: "1.15rem" }}>{t("import.title")}</h2>
        <p style={{ margin: "0.35rem 0 0", opacity: 0.75, fontSize: "0.95rem" }}>
          {t("import.intro")}
        </p>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
        <button type="button" onClick={() => fileInput.current?.click()} style={ghostButton}>
          {t("import.chooseFile")}
        </button>
        <button type="button" onClick={() => setText(EXAMPLE)} style={ghostButton}>
          {t("import.pasteExample")}
        </button>
        <input
          ref={fileInput}
          type="file"
          accept="application/json,.json"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void readFile(file);
            e.target.value = "";
          }}
          data-testid="quiz-import-file"
        />
      </div>

      <label style={{ display: "grid", gap: "0.35rem" }}>
        <span style={{ fontWeight: 600, fontSize: "0.95rem" }}>{t("import.jsonLabel")}</span>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={12}
          spellCheck={false}
          placeholder={EXAMPLE}
          style={{ ...textInput, fontFamily: "ui-monospace, monospace", resize: "vertical" }}
        />
      </label>

      {errors.length > 0 && (
        <ul role="alert" style={{ ...errorText, margin: 0, paddingLeft: "1.1rem" }}>
          {errors.map((message) => (
            <li key={message}>{message}</li>
          ))}
        </ul>
      )}

      <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
        <button
          type="button"
          className="btn-tactile"
          onClick={() => void handleImport()}
          disabled={isImporting || text.trim().length === 0}
          style={{
            ...primaryButton,
            opacity: isImporting || text.trim().length === 0 ? 0.6 : 1,
          }}
        >
          {isImporting ? t("import.importing") : t("import.importQuiz")}
        </button>
        <button type="button" onClick={onCancel} style={ghostButton}>
          {t("common.cancel")}
        </button>
      </div>
    </section>
  );
}
